/**
 * USAGE: this can be used two ways:
 *  1. As a full page route (legacy) — if no `onClose` prop is passed, picking
 *     a city falls back to `navigate(-1)` to pop the route.
 *  2. Inside a modal (e.g. CustomerHeader) — pass `onClose` and it will be
 *     called after dispatching the city selection instead of navigating.
 *
 * FIX: city search only exists as a province-scoped lookup
 * (`getFilteredCities(provinceName, searchText)`) — there is no free-text
 * "search every city regardless of province" endpoint anywhere in the app.
 * This previously tried to search without a province and always fell back
 * to an empty result set (see the removed onChangeSearch stub). Ported the
 * same province-select-then-search flow that already works correctly in
 * CityPickerButtonModal, reusing the identical redux actions/selectors.
 */
import React, { useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { ChevronRight, Clock, MapPin, Search } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { actions as cityActions } from 'coupon-common/src/modules/cities';
import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';

import { Modal } from 'components';
import ThemedSelect from 'components/ThemedSelect/ThemedSelect';

import styles from './LocationSearch.module.scss';
import messages from './messages';

const DEFAULT_CITY_STORAGE_KEY = 'DEFAULT_CITY';
const provincesKey = locationsDuck.provincesEntity.key;
const citiesKey = locationsDuck.citiesEntity.key;

// Most-recently-picked cities, newest first, shown before a province is
// chosen so the user can jump straight back to one.
const RECENT_CITIES_STORAGE_KEY = 'RECENT_CITIES';
const MAX_RECENT_CITIES = 4;

const readRecentCities = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RECENT_CITIES_STORAGE_KEY));
    if (Array.isArray(stored)) return stored;
    // First run: seed with the city already saved as the default, if any.
    const defaultCity = JSON.parse(localStorage.getItem(DEFAULT_CITY_STORAGE_KEY));
    return defaultCity?.id ? [{ id: defaultCity.id, name: defaultCity.name }] : [];
  } catch (e) {
    return [];
  }
};

const saveRecentCity = item => {
  const updated = [
    { id: item.id, name: item.name },
    ...readRecentCities().filter(c => String(c.id) !== String(item.id)),
  ].slice(0, MAX_RECENT_CITIES);
  try {
    localStorage.setItem(RECENT_CITIES_STORAGE_KEY, JSON.stringify(updated));
  } catch (e) {
    console.warn('Could not persist recent cities to localStorage', e);
  }
};

const LocationSearch = ({ onClose }) => {
  const [provinceId, setProvinceId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [recentCities] = useState(readRecentCities);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    dispatch(locationActions.getProvinces());
  }, [dispatch]);

  const locations = useSelector(state => state.locations);
  const provinceOptions = locations.result?.[provincesKey]?.results?.map(id => locations.entities[provincesKey]?.[id]) || [];

  const citiesState = useSelector(state => state.locations.result?.[citiesKey]);
  const cityEntities = useSelector(state => state.locations.entities?.[citiesKey]) || {};
  const results = citiesState?.results?.map(id => cityEntities[id]).filter(Boolean) || [];

  // Same pattern as CityPickerButtonModal: re-fetch whenever the chosen
  // province or the search text changes. No-ops until a province is picked.
  useEffect(() => {
    const province = provinceOptions.find(p => String(p?.id) === String(provinceId));
    if (province) {
      dispatch(locationActions.getFilteredCities(province.name, searchValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provinceId, searchValue]);

  const onPressItem = item => {
    dispatch(cityActions.setActiveCity(item.name, item.id));
    try {
      localStorage.setItem(DEFAULT_CITY_STORAGE_KEY, JSON.stringify(item));
    } catch (e) {
      console.warn('Could not persist default city to localStorage', e);
    }
    saveRecentCity(item);
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  return (
    <div styleName={onClose ? 'page in-modal' : 'page'}>
      <label styleName="field-label" htmlFor="location-search-province">
        Province
      </label>
      <ThemedSelect
        id="location-search-province"
        value={provinceId}
        placeholder="Select a province"
        options={provinceOptions.map(p => ({ value: String(p.id), label: p.name }))}
        onChange={value => {
          setProvinceId(value);
          setSearchValue('');
        }}
        triggerClassName={styles['province-trigger']}
      />

      <label styleName="field-label" htmlFor="location-search-city">
        City
      </label>
      <div styleName={provinceId ? 'search-bar' : 'search-bar disabled'}>
        <Search size={16} styleName="search-icon" aria-hidden="true" />
        <input
          id="location-search-city"
          type="text"
          placeholder={provinceId ? intl.formatMessage(messages.search) : 'Select a province first'}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value.trim())}
          styleName="search-input"
          disabled={!provinceId}
        />
      </div>

      {/* Before a province is picked this section lists recently selected
          cities; once one is picked it switches to the search results. */}
      <p styleName="result-text">
        <FormattedMessage {...(provinceId ? messages.searchResultText : messages.recentSelectText)} />
      </p>

      <div styleName="results-list">
        {!provinceId && recentCities.length === 0 && (
          <div styleName="empty-state">
            <MapPin size={22} />
            <p>No recent cities yet. Pick a province to find yours.</p>
          </div>
        )}
        {!provinceId && recentCities.map(item => (
          <button key={item.id} type="button" styleName="result-item" onClick={() => onPressItem(item)}>
            <Clock size={16} styleName="result-icon" />
            <span styleName="result-name">{item.name}</span>
            <ChevronRight size={16} styleName="result-arrow" />
          </button>
        ))}
        {provinceId && results.length === 0 && searchValue && (
          <div styleName="empty-state">
            <Search size={22} />
            <p>No cities found.</p>
          </div>
        )}
        {provinceId && results.map(item => (
          <button key={item.id} type="button" styleName="result-item" onClick={() => onPressItem(item)}>
            <MapPin size={16} styleName="result-icon" />
            <span styleName="result-name">{item.name}</span>
            <ChevronRight size={16} styleName="result-arrow" />
          </button>
        ))}
      </div>
    </div>
  );
};

const ThemedLocationSearch = CSSModule(LocationSearch, styles, { allowMultiple: true });

/**
 * The "Change Location" modal — the shared Modal with the project-themed
 * header/card applied, wrapping LocationSearch. Used by CustomerHeader and
 * WebsiteBlogs.
 */
export const LocationSearchModal = ({ isOpen, onClose }) => (
  <Modal isOpen={isOpen} onRequestClose={onClose} title="Change Location" size="sm" className={styles['location-modal']}>
    <ThemedLocationSearch onClose={onClose} />
  </Modal>
);

export default ThemedLocationSearch;
